import React, { useContext, useState, useEffect, useRef } from 'react';
import { AxiosResponse } from 'axios';
import { Typography, Button, Tag, Checkbox } from 'antd';
import { AuthContext, AuthContextState } from '../../context/AuthContext';
import {
  getProblemSets,
  ProblemSetNestedResponse
} from '../../api/problems';
import { DIFFICULTY_MAP, DifficultyConfig } from '../../config/constants';
import MainLayout from '../../components/MainLayout';
import styles from './ProblemSets.module.css';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import ProblemSetsCollapse, { ProblemFilter } from '../../components/ProblemSetsCollapse';

export interface ProblemSetsProps {};

// shows list of problem sets and problems
// marks solved problems as completed
// shows number of solvers for each problem
// allows problem filtering by difficulty
const ProblemSets = (props: ProblemSetsProps): JSX.Element | null => {
  // fetched list of problem sets
  const [problemSets, setProblemSets] = useState<ProblemSetNestedResponse[]>([]);
  // ref to avoid fetching problem sets data multiple times on multiple renders
  const fetchProblemSetsDataCalled = useRef<boolean>(false);
  // array of keys of expanded panels
  const [expandedPanels, setExpandedPanels] = useState<string[]>([]);
  // problem filter object
  const [problemFilter, setProblemFilter] = useState<ProblemFilter>({
    Beginner: true,
    Intermediate: true,
    Advanced: true
  });
  // get refresh request function from auth context
  const { refreshRetryRequest } = useContext(AuthContext) as AuthContextState;

  // set document title
  useEffect(() => {
    document.title = 'Problem Sets | Sigmoid Academy';
  }, []);

  // get problem sets and solved problems on render
  useEffect(() => {
    if (fetchProblemSetsDataCalled.current) {
      return;
    }

    fetchProblemSetsDataCalled.current = true;

    const onSuccess = (response: AxiosResponse) => {
      setProblemSets(response.data as ProblemSetNestedResponse[]);
      setExpandedPanels(response.data.map((ps: ProblemSetNestedResponse) => ps.id.toString()));
    };

    // get problem sets
    refreshRetryRequest(
      getProblemSets
    ).then(
      onSuccess
    ).catch((error: any) => {
      // get problem sets without access token if previous call fails
      getProblemSets(
        null
      ).then(
        onSuccess
      ).catch((error: any) => console.log('Error in getting problem sets'));
    });
  }, [refreshRetryRequest]);

  return (
    <div>
      <MainLayout selectedKey='problem-sets'>
        <div className={styles.mainContainer}>
          <div className={styles.mainHeader}>
            <div className={styles.headingContainer}>
              <Typography.Title
                level={1}
                className={styles.mainHeading}
              >
                Problem Sets
              </Typography.Title>
            </div>
            <div className={styles.actionsContainer}>
              <div className={styles.filtersContainer}>
                {Object.values(DIFFICULTY_MAP).map((config: DifficultyConfig) => (
                  <Checkbox
                    key={config.name}
                    className={problemFilter[config.name] ? styles.problemFilterCheckboxChecked : styles.problemFilterCheckboxUnchecked}
                    checked={problemFilter[config.name]}
                    onChange={(e: CheckboxChangeEvent) => setProblemFilter((pf: ProblemFilter): ProblemFilter => {
                      const newPF = {
                        ...pf
                      };
                      newPF[config.name] = e.target.checked;

                      return newPF;
                    })}
                  >
                    <Tag className={config.tagClass}>
                      {config.name}
                    </Tag>
                  </Checkbox>
                ))}
              </div>
              <div className={styles.collapseExpandButtonsContainer}>
                <Button
                  type='primary'
                  className='mainButton'
                  onClick={() => setExpandedPanels([])}
                >
                  Collapse All
                </Button>
                <Button
                  type='primary'
                  className='mainButton'
                  onClick={() => setExpandedPanels(problemSets.map((ps: ProblemSetNestedResponse) => ps.id.toString()))}
                >
                  Expand All
                </Button>
              </div>
            </div>
          </div>
          <ProblemSetsCollapse
            problemSets={problemSets}
            filter={problemFilter}
            expanded={expandedPanels}
            onChange={setExpandedPanels}
          />
        </div>
      </MainLayout>
    </div>
  );
};

export default ProblemSets;
